import React, { useState } from "react";
import {
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import API from "../../api/axiosConfig";

const RateUpdateButton = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [rateType, setRateType] = useState("HEALTH"); // 'HEALTH' or 'ANCILLARY'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ type: "", message: "" });

  // use to select year for rate sheet
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  // Initialize state with currentYear as default
  const [effectiveYear, setEffectiveYear] = useState(currentYear);

  //  Confirmation Dialog State (confirm overwrite)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setRateType("HEALTH");
      setUploadStatus({ type: "", message: "" });
      setOpen(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        setUploadStatus({
          type: "error",
          message: "Please select a valid .csv file.",
        });
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
        setUploadStatus({ type: "", message: "" });
      }
    }
  };

  const handleUploadSubmit = async (overwrite = false) => {
    if (!selectedFile) {
      setUploadStatus({
        type: "error",
        message: "Please select a CSV file to upload.",
      });
      return;
    }
    if (!effectiveYear) {
      setUploadStatus({
        type: "error",
        message: "Please select an effective year for the rate sheet",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("type", rateType); // Sends 'HEALTH', 'ANCILLARY' to backend
    formData.append("effectiveYear", effectiveYear);
    formData.append("overwrite", overwrite);

    try {
      setUploading(true);
      setUploadStatus({ type: "", message: "" });

      await API.post("/api/insurance/rates/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploadStatus({
        type: "success",
        message: `${
          rateType.charAt(0) + rateType.slice(1).toLowerCase()
        } rates updated successfully!`,
      });

      setTimeout(() => {
        handleClose();
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err) {
      if (
        err.response?.status === 409 &&
        err.response?.data.requiresConfirmation
      ) {
        // this means duplicate exists in DB.  ask permission to overwrite by opening dialog
        setConfirmMessage(
          err.response.data.message ||
            `Rates for ${effectiveYear} already exist. Overwrite? (Warning: This will still force everyone to re enroll in their insurance plans)`,
        ); 
        setConfirmOpen(true);
        return;
      }
      console.error(err);
      setUploadStatus({
        type: "error",
        message:
          err.response?.data?.message ||
          "Failed to upload rates file. Please check the CSV format.",
      });
    } finally {
      setUploading(false);
    }
  };

  // Confirm & Cancel handlers for Overwrite Dialog
  const handleConfirmOverwrite = () => {
    setConfirmOpen(false);
    handleUploadSubmit(true); // Re-trigger upload
  };

  const handleCancelOverwrite = () => {
    setConfirmOpen(false);
  };

  // Helper text explaining required columns per selection
  const getExpectedColumnsText = () => {
    switch (rateType) {
      case "HEALTH":
        return "Expected CSV format: age, bronze, silver, platinum";
      case "ANCILLARY":
        return "Expected CSV format: type, individual cost, with child cost, with adult cost, family cost)";
      default:
        return "";
    }
  };

  return (
    <>
      {/* Header Action Button */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<UploadFileIcon />}
        onClick={handleOpen}
        sx={{ fontWeight: "bold", textTransform: "none", borderRadius: 2 }}
      >
        Update Rate Sheet
      </Button>

      {/* Structured Upload Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: 8,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Upload Insurance Rate Sheet
          </Typography>
          <IconButton onClick={handleClose} disabled={uploading} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "divider", py: 3 }}>
          <Stack spacing={2.5}>
            {/* Rate Type Selector Dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel id="rate-type-select-label">
                Insurance Rate Type
              </InputLabel>
              <Select
                labelId="rate-type-select-label"
                value={rateType}
                label="Insurance Type"
                onChange={(e) => setRateType(e.target.value)}
                disabled={uploading}
              >
                <MenuItem value="HEALTH">Health</MenuItem>
                <MenuItem value="ANCILLARY">Dental/Vision</MenuItem>
              </Select>
              <FormHelperText sx={{ mt: 1 }}>
                {getExpectedColumnsText()}
              </FormHelperText>
            </FormControl>

            {/* Effective Year Selector Dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel id="effective-year-select-label">
                Effective Year
              </InputLabel>
              <Select
                labelId="effective-year-select-label"
                value={effectiveYear}
                label="Effective Year"
                onChange={(e) => setEffectiveYear(e.target.value)}
                disabled={uploading}
              >
                <MenuItem value={currentYear}>{currentYear}</MenuItem>
                <MenuItem value={nextYear}>{nextYear}</MenuItem>
              </Select>
            </FormControl>

            {/* Alert Banner */}
            {uploadStatus.message ? (
              <Alert severity={uploadStatus.type} sx={{ borderRadius: 2 }}>
                {uploadStatus.message}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {"Changing rate sheets will force all employees to re enroll in their insurance plans"}
              </Alert>
            )}

            {/* Clean File Dropzone */}
            <Paper
              variant="outlined"
              component="label"
              sx={{
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px dashed",
                borderColor: selectedFile ? "primary.main" : "divider",
                borderRadius: 2.5,
                textAlign: "center",
                bgcolor: selectedFile ? "action.hover" : "background.paper",
                cursor: uploading ? "default" : "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: uploading ? "divider" : "primary.main",
                  bgcolor: uploading ? "background.paper" : "action.hover",
                },
              }}
            >
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
                disabled={uploading}
              />

              {selectedFile ? (
                <InsertDriveFileIcon
                  sx={{ fontSize: 44, color: "primary.main", mb: 1 }}
                />
              ) : (
                <UploadFileIcon
                  sx={{ fontSize: 44, color: "text.secondary", mb: 1 }}
                />
              )}

              <Typography variant="subtitle1" fontWeight="bold">
                {selectedFile
                  ? selectedFile.name
                  : "Click to select a CSV file"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {selectedFile
                  ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                  : "Only .csv files are supported"}
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={uploading}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: "bold" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleUploadSubmit()}
            disabled={!selectedFile || uploading}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
              px: 3,
            }}
            startIcon={
              uploading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {uploading ? "Uploading..." : "Upload and Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Overwrite Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelOverwrite}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Overwrite Existing Rates?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelOverwrite}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: "bold" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOverwrite}
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: "bold", borderRadius: 2 }}
          >
            Overwrite Rates
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RateUpdateButton;
